import { useEffect, useState } from 'react'

import type { Temporal } from '@js-temporal/polyfill'

import { getZonedNow, startOfDay } from '~publish/helpers/temporal'

const ONE_MINUTE_MS = 60000

export type TimezonedNowInfo = {
  now: Temporal.ZonedDateTime
  nowTimestamp: number
  today: Temporal.ZonedDateTime
  todayTimestamp: number
}

function getTimezonedNowInfo(timezone: string): TimezonedNowInfo {
  const now = getZonedNow(timezone)
  const today = startOfDay(now)

  return {
    today,
    todayTimestamp: today.toInstant().epochMilliseconds,
    now,
    nowTimestamp: now.toInstant().epochMilliseconds,
  }
}

/**
 * A hook that provides the current time in a specified timezone.
 * Updates every minute to keep the time current.
 *
 * @param {string} timezone - The timezone identifier (e.g., 'America/New_York')
 * @returns {Object} An object containing:
 *   - now: The current time as a Temporal.ZonedDateTime
 *   - nowTimestamp: The current time as milliseconds since epoch
 *   - today: The start of the current day as a Temporal.ZonedDateTime
 *   - todayTimestamp: The start of the current day as milliseconds since epoch
 */
export function useTimezonedNow(timezone: string): TimezonedNowInfo {
  const [nowInfo, setNowInfo] = useState<TimezonedNowInfo>(() =>
    getTimezonedNowInfo(timezone),
  )

  useEffect(() => {
    setNowInfo(getTimezonedNowInfo(timezone))

    const interval = setInterval(() => {
      setNowInfo(getTimezonedNowInfo(timezone))
    }, ONE_MINUTE_MS)
    return (): void => clearInterval(interval)
  }, [timezone])

  return nowInfo
}
